/**
 * Is the user selecting text inside this element?
 *
 * Live-updating content destroys a selection: replacing the markup kills the
 * range with it. The GPU-timing tooltip says "measured 17s ago", so it rewrote
 * itself every second and copying anything out of it was impossible. The answer
 * here is what lets a view hold its update until the reader is done.
 *
 * A selection that STARTED inside and ran out (dragging past the edge) still
 * counts — dropping the guard mid-gesture would break exactly the drag being
 * protected. A collapsed range is a caret, not a selection: nothing to lose.
 */
export function isSelectionInside(
    element: Node | null | undefined,
    selection: { isCollapsed?: boolean; anchorNode?: Node | null; focusNode?: Node | null } | null | undefined,
): boolean {
    if (!element || !selection || selection.isCollapsed !== false) return false;
    return contains(element, selection.anchorNode) || contains(element, selection.focusNode);
}

function contains(element: Node, node: Node | null | undefined): boolean {
    return !!node && (element === node || element.contains(node));
}

/**
 * The selection of the tree an element lives in — a shadow root keeps its own
 * (the editor renders inside one), and `document.getSelection()` there reports
 * the shadow HOST, so the check would always miss.
 */
export function selectionForElement(element: Node | null | undefined): ReturnType<typeof document.getSelection> | null {
    if (!element) return null;
    const root = element.getRootNode?.() as (ShadowRoot | Document | null);
    const fromRoot = (root as { getSelection?: () => Selection | null })?.getSelection?.();
    return fromRoot ?? element.ownerDocument?.getSelection?.() ?? null;
}
